import React from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import apiService, { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface BinYarn { yarnName: string; color?: string; colorCode?: string; lot?: string; boxes: number; netWeight: number; }
interface Bin { location: string; section: string; totalBoxes: number; netWeight: number; grossWeight: number; yarns: BinYarn[]; }
interface Section { section: string; bins: Bin[]; totalBoxes: number; netWeight: number; }
interface DashboardData {
  summary: { totalBoxes: number; netWeight: number; binsUsed: number; sections: number };
  sections: Section[];
  bins: Bin[];
}
// A "group" is a set of section-rows drawn together (no gap between them); groups are
// separated by a gap. Matches the sketch: D alone, then C+B merged, then A alone.
interface GridConfig { binsPerSection: number; groups: string[][]; lowStockThreshold: number; }

const DEFAULT_CONFIG: GridConfig = { binsPerSection: 15, groups: [['D'], ['C', 'B'], ['A']], lowStockThreshold: 0 };
const MAX_BINS = 60;

const fmt = (n: number) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const numOf = (loc: string) => { const m = String(loc).match(/(\d+)/); return m ? parseInt(m[1], 10) : 0; };
const sectionOf = (loc: string) => (String(loc).toUpperCase().match(/^[A-Z]+/) || ['?'])[0];
// "D / C, B / A"  →  [['D'], ['C','B'], ['A']]   ( / = gap between blocks, comma = merged )
const parseGroups = (s: string): string[][] => {
  const seen = new Set<string>();
  return s.split('/')
    .map(part => (part.toUpperCase().match(/[A-Z]+/g) || []).filter(x => (seen.has(x) ? false : (seen.add(x), true))))
    .filter(g => g.length);
};
const groupsToString = (groups: string[][]) => groups.map(g => g.join(', ')).join('  /  ');

// Download the box-history workbook (whole warehouse when `query` is empty, or a single
// bin with `?location=...`). Uses the raw axios instance so the auth token is attached and
// the response comes back as a binary blob.
async function downloadHistoryExcel(query: string, filename: string) {
  const res = await api.get(`/yarn-masters/dashboard/export${query}`, { responseType: 'blob' });
  const contentType = (res.headers['content-type'] as string) || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const url = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// Build the export query (?location&from&to) and a filename that encodes the date range.
// Empty from/to → all history (whole-warehouse or single bin).
function buildExportQuery(opts: { location?: string; from?: string; to?: string }) {
  const params: string[] = [];
  if (opts.location) params.push(`location=${encodeURIComponent(opts.location)}`);
  if (opts.from) params.push(`from=${opts.from}`);
  if (opts.to) params.push(`to=${opts.to}`);
  const base = opts.location ? `bin-${opts.location}-history` : 'yarn-warehouse-history';
  const suffix = (opts.from || opts.to) ? `_${opts.from || 'start'}_to_${opts.to || 'end'}` : '';
  return { query: params.length ? `?${params.join('&')}` : '', filename: `${base}${suffix}.xlsx` };
}

// Download the current-stock "YARN STOCK" report — a colour-wise snapshot across warehouse
// sections. `format` picks the styled .xlsx (ExcelJS) or the printable .pdf (server-rendered).
async function downloadStockReport(format: 'xlsx' | 'pdf') {
  const res = await api.get(`/yarn-masters/dashboard/stock-export?format=${format}`, { responseType: 'blob' });
  const fallbackType = format === 'pdf'
    ? 'application/pdf'
    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const contentType = (res.headers['content-type'] as string) || fallbackType;
  const url = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `yarn-stock-report.${format}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// Compact From–To date range picker shared by both export spots.
const DateRange: React.FC<{ from: string; to: string; onFrom: (v: string) => void; onTo: (v: string) => void }> = ({ from, to, onFrom, onTo }) => (
  <div className="flex items-center gap-1.5" title="Optional — leave empty to export all history">
    <input type="date" value={from} max={to || undefined} onChange={(e) => onFrom(e.target.value)}
      className="px-2 py-1.5 text-sm text-gray-600 bg-white border border-[#E1E6EF] rounded-lg focus:outline-none focus:border-[#0055a9]" />
    <span className="text-xs text-gray-400">–</span>
    <input type="date" value={to} min={from || undefined} onChange={(e) => onTo(e.target.value)}
      className="px-2 py-1.5 text-sm text-gray-600 bg-white border border-[#E1E6EF] rounded-lg focus:outline-none focus:border-[#0055a9]" />
  </div>
);

// Small reusable "Export to Excel" button (spreadsheet icon + label), used on the
// dashboard header and inside the bin history modal.
const ExcelButton: React.FC<{ onClick: () => void; loading: boolean; label: string }> = ({ onClick, loading, label }) => (
  <button onClick={onClick} disabled={loading}
    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-emerald-700 bg-white border border-emerald-600 rounded-xl hover:bg-emerald-50 disabled:opacity-50 transition-colors">
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
    {loading ? 'Exporting…' : label}
  </button>
);

// Red "Export to PDF" button (document icon), used for the printable stock report.
const PdfButton: React.FC<{ onClick: () => void; loading: boolean; label: string }> = ({ onClick, loading, label }) => (
  <button onClick={onClick} disabled={loading}
    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-red-700 bg-white border border-red-600 rounded-xl hover:bg-red-50 disabled:opacity-50 transition-colors">
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
    {loading ? 'Exporting…' : label}
  </button>
);

const StatCard: React.FC<{ label: string; value: string; accent: string; note?: string }> = ({ label, value, accent, note }) => (
  <div className="bg-white rounded-xl border border-[#E8EBF1] px-4 py-3">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-[rgba(14,19,27,0.45)]">{label}</p>
    <p className={`text-2xl font-bold tabular-nums ${accent}`}>{value}</p>
    {note && <p className="text-[11px] text-[rgba(14,19,27,0.45)] mt-0.5">{note}</p>}
  </div>
);

const LOW_BOX = 4; // a stocked bin at or below this box count is "running low"

// Only two states carry colour: RED = running low, and a dashed outline = empty bin.
// Every other bin stays plain white so the map isn't a wall of colour.
const binShade = (count: number, netLow = false) => {
  if (count === 0) return { bg: 'bg-[#FAFBFC]', border: 'border-dashed border-[#D8DEE8]', code: 'text-[rgba(14,19,27,0.3)]', badge: 'bg-[rgba(14,19,27,0.06)] text-[rgba(14,19,27,0.4)]', wt: 'text-[rgba(14,19,27,0.3)]', low: false };
  if (count <= LOW_BOX || netLow) return { bg: 'bg-red-50', border: 'border-red-300', code: 'text-red-700', badge: 'bg-red-600 text-white', wt: 'text-red-600', low: true };
  return { bg: 'bg-white', border: 'border-[#E1E6EF]', code: 'text-[#0055a9]', badge: 'bg-[#0055a9] text-white', wt: 'text-emerald-700', low: false };
};

// One warehouse slot (a cell in a section row).
const BinCell: React.FC<{ id: string; bin?: Bin; onClick?: (loc: string) => void; threshold?: number }> = ({ id, bin, onClick, threshold = 0 }) => {
  const clickable = !!bin && !!onClick;
  const multi = (bin?.yarns.length || 0) > 1; // show per-yarn box counts only when a bin holds >1 yarn
  // Net-weight low alert (configurable) still forces the red tier; otherwise colour by fullness.
  const netLow = !!bin && bin.totalBoxes > 0 && threshold > 0 && bin.netWeight < threshold;
  const sh = binShade(bin ? bin.totalBoxes : 0, netLow);
  const isLow = sh.low;
  const hover = clickable ? 'cursor-pointer hover:outline hover:outline-2 hover:outline-[#0055a9] hover:outline-offset-[-2px] transition-all' : '';
  // Meta badge: lots count / LOW / (empty bins show nothing extra).
  const meta = !bin ? '' : bin.totalBoxes === 0 ? '' : isLow ? 'LOW' : (bin.yarns.length > 1 ? `${bin.yarns.length} LOTS` : '1 LOT');
  return (
    <div onClick={() => clickable && onClick!(id)}
      title={clickable ? (isLow ? `Running low — ${bin!.totalBoxes} box${netLow ? `, below ${fmt(threshold)} kg` : ''}. View history` : 'View stock & dispatch history') : undefined}
      className={`w-32 shrink-0 border p-2 flex flex-col min-h-[78px] rounded-lg m-[3px] ${sh.border} ${sh.bg} ${hover}`}>
      <div className="flex items-center justify-between gap-1">
        <span className={`text-[11px] font-bold tabular-nums ${sh.code}`}>{id}</span>
        {bin && bin.totalBoxes > 0 && <span className={`shrink-0 text-[10px] font-bold px-1.5 py-[1px] rounded-full tabular-nums leading-none ${sh.badge}`}>{bin.totalBoxes} box</span>}
      </div>
      {bin && bin.yarns.length > 0 ? (
        <>
          {bin.yarns.map((y, idx) => {
            const cl = [y.color, y.colorCode, y.lot ? `Lot ${y.lot}` : ''].filter(Boolean).join(' · ');
            const chip = [cl, multi ? `${y.boxes} box` : ''].filter(Boolean).join(' · ');
            return (
              <div key={idx} className={idx > 0 ? 'mt-1.5 pt-1.5 border-t border-black/[0.06]' : 'mt-1'}>
                <span className="block text-[11px] font-semibold text-[#0E131B] leading-tight line-clamp-2" title={y.yarnName}>{y.yarnName}</span>
                {chip && (
                  <span className="mt-0.5 inline-block max-w-full text-[10px] font-semibold text-[#475569] bg-[#F1F5F9] rounded px-1.5 py-[1px] leading-snug break-words" title={chip}>{chip}</span>
                )}
              </div>
            );
          })}
          <div className="mt-auto pt-1 flex items-baseline justify-between gap-1">
            {meta && <span className={`text-[8.5px] font-bold uppercase tracking-wider ${isLow ? 'text-red-500' : 'text-[rgba(14,19,27,0.4)]'}`}>{meta}</span>}
            <span className={`ml-auto text-[11px] font-bold tabular-nums ${sh.wt}`}>{fmt(bin.netWeight)}</span>
          </div>
        </>
      ) : null}
    </div>
  );
};

// Modal: a bin's stock summary + its approved outward (dispatch) history.
const BoxHistoryModal: React.FC<{ location: string; onClose: () => void }> = ({ location, onClose }) => {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['boxHistory', location],
    queryFn: async () => await apiService.get(`/yarn-masters/dashboard/history?location=${encodeURIComponent(location)}`),
  });
  const [exporting, setExporting] = React.useState(false);
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  // Group the bin's boxes by yarn + color so multi-yarn bins read cleanly (one header per
  // yarn/color, its boxes below, with an in-stock count + subtotal). Consumed boxes still show.
  const boxGroups = React.useMemo(() => {
    const m = new Map<string, any>();
    // Space-insensitive, case-insensitive key so "2325-FENTA" and "2325 - FENTA" group together;
    // the first-seen original values are what get displayed.
    const norm = (s: any) => String(s || '').replace(/\s+/g, '').toUpperCase();
    (data?.boxes || []).forEach((b: any) => {
      const key = `${norm(b.yarnName) || '—'}||${norm(b.color)}||${norm(b.colorCode)}||${norm(b.lot)}`;
      if (!m.has(key)) m.set(key, { yarnName: b.yarnName || '—', color: b.color || '', colorCode: b.colorCode || '', lot: b.lot || '', boxes: [], activeNet: 0, activeCount: 0 });
      const g = m.get(key);
      g.boxes.push(b);
      if (!b.isConsumed) { g.activeNet += Number(b.netWeight) || 0; g.activeCount += 1; }
    });
    return Array.from(m.values());
  }, [data]);
  const exportBin = async () => {
    setExporting(true);
    try {
      const { query, filename } = buildExportQuery({ location, from, to });
      await downloadHistoryExcel(query, filename);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8EBF1]">
          <div>
            <h3 className="text-lg font-bold text-[#1B202A]">Bin {location}</h3>
            <p className="text-xs text-[rgba(14,19,27,0.5)]">Stock &amp; dispatch history</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F8F9FC] text-gray-500">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {isLoading ? (
            <div className="p-6 text-center"><div className="animate-spin rounded-full h-7 w-7 border-2 border-[#0055a9] border-t-transparent mx-auto" /></div>
          ) : (
            <>
              {/* Stock at a glance — a bar makes the reduction instantly readable */}
              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <p className="tabular-nums">
                    <span className="text-[28px] leading-none font-bold text-emerald-700">{fmt(data?.currentNet || 0)}</span>
                    <span className="text-sm font-semibold text-[rgba(14,19,27,0.45)] ml-1.5">kg in stock</span>
                  </p>
                  <p className="text-xs text-[rgba(14,19,27,0.45)]">of {fmt(data?.originalNet || 0)} kg</p>
                </div>
                <div className="h-2.5 rounded-full bg-orange-200/70 overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ width: `${(data?.originalNet || 0) > 0 ? Math.max(0, Math.min(100, ((data.currentNet || 0) / data.originalNet) * 100)) : (data?.currentNet ? 100 : 0)}%` }} />
                </div>
                <div className="flex items-center justify-between mt-1.5 text-[11px] font-semibold">
                  <span className="text-emerald-600 flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" />In stock {fmt(data?.currentNet || 0)}</span>
                  <span className="text-orange-500 flex items-center gap-1">Dispatched {fmt(data?.dispatchedNet || 0)}<span className="w-2 h-2 rounded-sm bg-orange-400" /></span>
                </div>
              </div>

              {/* Boxes in this bin — grouped by yarn + color */}
              {(data?.boxes || []).length > 0 && (
                <div>
                  <p className="text-[11px] font-bold text-[rgba(14,19,27,0.4)] uppercase tracking-wider mb-2">Boxes in this bin · {data.boxes.length}</p>
                  <div className="space-y-2.5">
                    {boxGroups.map((g: any, gi: number) => {
                      const colorLabel = [g.color, g.colorCode, g.lot ? `Lot ${g.lot}` : ''].filter(Boolean).join(' · ');
                      return (
                        <div key={gi} className="rounded-xl border border-[#E8EBF1] overflow-hidden">
                          {/* group header: yarn + color, with in-stock count + subtotal */}
                          <div className="flex items-center justify-between gap-2 px-3 py-2 bg-[#F8F9FC] border-b border-[#E8EBF1]">
                            <div className="min-w-0 flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-[#0E131B]">{g.yarnName}</span>
                              {colorLabel && (
                                <span className="text-[10px] font-semibold text-[#475569] bg-white border border-[#E1E6EF] rounded px-1.5 py-[1px] break-words" title={colorLabel}>{colorLabel}</span>
                              )}
                            </div>
                            <span className="shrink-0 text-[11px] font-bold text-[#0055a9] tabular-nums">{g.activeCount} box · {fmt(g.activeNet)} kg</span>
                          </div>
                          {/* box rows */}
                          <div className="divide-y divide-[#F1F3F9]">
                            {g.boxes.map((b: any, i: number) => (
                              <div key={i} className="flex items-center gap-3 px-3 py-2">
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg shrink-0 ${b.isConsumed ? 'bg-gray-100 text-gray-400' : 'bg-[#0055a9]/10 text-[#0055a9]'}`}>
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                                </span>
                                <span className="flex-1 text-sm font-semibold text-[#0E131B] tabular-nums">{b.boxNo}</span>
                                <span className={`text-sm font-bold tabular-nums shrink-0 ${b.isConsumed ? 'text-gray-400' : 'text-emerald-700'}`}>{b.isConsumed ? 'Out' : `${fmt(b.netWeight)} kg`}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Dispatch history timeline */}
              <div>
                <p className="text-[11px] font-bold text-[rgba(14,19,27,0.4)] uppercase tracking-wider mb-2">Dispatch history</p>
                {(data?.history || []).length === 0 ? (
                  <div className="text-center py-4 text-xs text-[rgba(14,19,27,0.4)] bg-[#F8F9FC] rounded-xl">No dispatches yet — full quantity in stock.</div>
                ) : (
                  <div className="relative pl-5">
                    <div className="absolute left-[6px] top-1.5 bottom-1.5 w-px bg-[#E8EBF1]" />
                    <div className="space-y-3.5">
                      {data.history.map((h: any, i: number) => (
                        <div key={i} className="relative">
                          <span className="absolute -left-5 top-1 w-3 h-3 rounded-full bg-orange-400 ring-2 ring-white" />
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-[#0E131B] truncate">→ {h.buyer || '—'}</p>
                              <p className="text-[11px] text-[rgba(14,19,27,0.45)]">Box {h.boxNo}{h.challanNo ? ` · Challan ${h.challanNo}` : ''} · {h.date ? String(h.date).slice(0, 10) : ''}</p>
                            </div>
                            <span className="text-sm font-bold text-orange-600 tabular-nums shrink-0">−{fmt(h.weight)} kg</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-[#E8EBF1] flex items-center justify-between gap-3 flex-wrap shrink-0">
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
          <ExcelButton onClick={exportBin} loading={exporting} label="Export to Excel" />
        </div>
      </div>
    </div>,
    document.body
  );
};

const YarnDashboard: React.FC = () => {
  const { user } = useAuth() as { user: any };
  const isAdmin = user?.role === 'SUPERADMIN' || user?.role === 'ADMIN';
  const { data, isLoading, isError, refetch, isFetching } = useQuery<DashboardData>({
    queryKey: ['yarnDashboard'],
    queryFn: async (): Promise<DashboardData> => await apiService.get('/yarn-masters/dashboard'),
    refetchOnWindowFocus: false,
  });

  // ─── Grid config (bins per section + grouped sections) — saved per user on the server ───
  const [config, setConfig] = React.useState<GridConfig>(DEFAULT_CONFIG);
  const [showConfig, setShowConfig] = React.useState(false);
  const [draftBins, setDraftBins] = React.useState(String(DEFAULT_CONFIG.binsPerSection));
  const [draftGroups, setDraftGroups] = React.useState(groupsToString(DEFAULT_CONFIG.groups));
  const [draftThreshold, setDraftThreshold] = React.useState(String(DEFAULT_CONFIG.lowStockThreshold));
  const [historyLoc, setHistoryLoc] = React.useState<string | null>(null);
  const [exportingAll, setExportingAll] = React.useState(false);
  const [mobileSection, setMobileSection] = React.useState<string>(''); // active section tab on mobile
  const [exportFrom, setExportFrom] = React.useState('');
  const [exportTo, setExportTo] = React.useState('');
  const handleExportAll = async () => {
    setExportingAll(true);
    try {
      const { query, filename } = buildExportQuery({ from: exportFrom, to: exportTo });
      await downloadHistoryExcel(query, filename);
    } catch {
      toast.error('Export failed');
    } finally {
      setExportingAll(false);
    }
  };

  // Current-stock "YARN STOCK" report (snapshot — no date range), in Excel or PDF.
  const [stockXlsxLoading, setStockXlsxLoading] = React.useState(false);
  const [stockPdfLoading, setStockPdfLoading] = React.useState(false);
  const handleStockReport = async (format: 'xlsx' | 'pdf') => {
    const setLoading = format === 'pdf' ? setStockPdfLoading : setStockXlsxLoading;
    setLoading(true);
    try {
      await downloadStockReport(format);
    } catch {
      toast.error('Stock report export failed');
    } finally {
      setLoading(false);
    }
  };

  // Company-wide layout arrives with the dashboard data (set by an admin, applied to everyone).
  React.useEffect(() => {
    const saved = (data as any)?.dashboardConfig;
    if (!saved || !saved.binsPerSection) return;
    const groups: string[][] = Array.isArray(saved.groups) && saved.groups.length
      ? saved.groups.filter((g: any) => Array.isArray(g) && g.length)
      : (Array.isArray(saved.sections) && saved.sections.length ? saved.sections.map((s: string) => [s]) : []);
    if (!groups.length) return;
    const lowStockThreshold = Math.max(0, Number(saved.lowStockThreshold) || 0);
    const cfg = { binsPerSection: Math.min(MAX_BINS, Math.max(1, Number(saved.binsPerSection) || 15)), groups, lowStockThreshold };
    setConfig(cfg);
    setDraftBins(String(cfg.binsPerSection));
    setDraftGroups(groupsToString(cfg.groups));
    setDraftThreshold(String(lowStockThreshold));
  }, [data]);

  // Saved company-wide (admins only). Refetch so the new layout is reflected immediately.
  const savePrefsMutation = useMutation({
    mutationFn: async (cfg: GridConfig) => await apiService.put('/admin-settings', { yarnDashboardConfig: cfg }),
    onSuccess: () => { toast.success('Layout saved for everyone'); refetch(); },
    onError: () => toast.error('Could not save layout'),
  });

  const applyConfig = () => {
    const groups = parseGroups(draftGroups);
    const binsPerSection = Math.min(MAX_BINS, Math.max(1, parseInt(draftBins, 10) || DEFAULT_CONFIG.binsPerSection));
    if (!groups.length) { toast.error('Enter at least one section (e.g. A)'); return; }
    const lowStockThreshold = Math.max(0, parseFloat(draftThreshold) || 0);
    const cfg = { binsPerSection, groups, lowStockThreshold };
    setConfig(cfg);
    setDraftBins(String(binsPerSection));
    setDraftGroups(groupsToString(groups));
    setDraftThreshold(String(lowStockThreshold));
    savePrefsMutation.mutate(cfg);
    setShowConfig(false);
  };
  const resetConfig = () => {
    setDraftBins(String(DEFAULT_CONFIG.binsPerSection));
    setDraftGroups(groupsToString(DEFAULT_CONFIG.groups));
    setDraftThreshold(String(DEFAULT_CONFIG.lowStockThreshold));
    setConfig(DEFAULT_CONFIG);
    savePrefsMutation.mutate(DEFAULT_CONFIG);
  };

  // Index every bin by "SECTION-NUMBER" so the grid can look each slot up.
  const binLookup = React.useMemo(() => {
    const map: Record<string, Bin> = {};
    (data?.bins || []).forEach((b: Bin) => { map[`${sectionOf(b.location)}-${numOf(b.location)}`] = b; });
    return map;
  }, [data]);

  const gridSections = React.useMemo(() => config.groups.flat(), [config]);

  // Anything that doesn't fit the configured grid is surfaced separately (never silently dropped).
  const otherBins = React.useMemo(() =>
    (data?.bins || []).filter((b: Bin) => !gridSections.includes(sectionOf(b.location)) || numOf(b.location) < 1 || numOf(b.location) > config.binsPerSection),
  [data, gridSections, config.binsPerSection]);

  const sectionTotals = (sec: string) => {
    const bins = (data?.bins || []).filter((b: Bin) => sectionOf(b.location) === sec && numOf(b.location) >= 1 && numOf(b.location) <= config.binsPerSection);
    return { boxes: bins.reduce((s: number, b: Bin) => s + b.totalBoxes, 0), net: bins.reduce((s: number, b: Bin) => s + b.netWeight, 0) };
  };

  const colNums = Array.from({ length: config.binsPerSection }, (_, k) => k + 1);

  // One section's horizontal row of slots (used inside a group block)
  const SectionRow: React.FC<{ sec: string; last: boolean }> = ({ sec, last }) => {
    const t = sectionTotals(sec);
    return (
      <div className={`flex ${last ? '' : 'border-b border-[#E8EBF1]'}`}>
        <div className="sticky left-0 z-10 bg-[#F8F9FC] w-24 shrink-0 border-r border-[#E8EBF1] px-2 py-1.5 flex flex-col justify-center">
          <span className="text-base font-bold text-[#0E131B]">{sec}</span>
          <span className="text-[9px] text-[rgba(14,19,27,0.45)] leading-tight">{sec}1–{sec}{config.binsPerSection}</span>
          <span className="text-[9px] font-semibold text-[#0055a9] mt-0.5 tabular-nums">{t.boxes} box · {fmt(t.net)}</span>
        </div>
        {colNums.map(n => <BinCell key={n} id={`${sec}-${n}`} bin={binLookup[`${sec}-${n}`]} onClick={setHistoryLoc} threshold={config.lowStockThreshold} />)}
      </div>
    );
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="px-2 py-4 space-y-4">
      {/* Toolbar (page name is already shown in the top bar) */}
      <div className="flex items-center justify-end flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Current-stock snapshot report (colour-wise, per warehouse section) */}
          <ExcelButton onClick={() => handleStockReport('xlsx')} loading={stockXlsxLoading} label="Stock Report · Excel" />
          <PdfButton onClick={() => handleStockReport('pdf')} loading={stockPdfLoading} label="Stock Report · PDF" />
          <span className="hidden sm:inline-block w-px h-6 bg-[#E1E6EF] mx-1" aria-hidden />
          {/* Movement-history export (date-range) */}
          <DateRange from={exportFrom} to={exportTo} onFrom={setExportFrom} onTo={setExportTo} />
          <ExcelButton onClick={handleExportAll} loading={exportingAll} label="Export All to Excel" />
          {isAdmin && (
          <button onClick={() => setShowConfig(v => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-[#E1E6EF] rounded-xl hover:bg-[#F8F9FC] transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            Configure grid
          </button>
          )}
          <button onClick={() => refetch()} disabled={isFetching}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-[#0055a9] bg-white border border-[#0055a9] rounded-xl hover:bg-[#F8F9FC] disabled:opacity-50 transition-colors">
            <svg className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Dynamic grid config */}
      {showConfig && (
        <div className="bg-white rounded-xl border border-[#E8EBF1] p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">Bins per section (1–{MAX_BINS})</label>
            <input type="number" min={1} max={MAX_BINS} value={draftBins} onChange={e => setDraftBins(e.target.value)}
              className="w-28 px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">Layout — merge with “,” · gap with “/”</label>
            <input type="text" value={draftGroups} onChange={e => setDraftGroups(e.target.value)} placeholder="D / C, B / A"
              className="w-64 px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white uppercase focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">Low-stock alert (kg) — 0 = off</label>
            <input type="number" min={0} step="any" value={draftThreshold} onChange={e => setDraftThreshold(e.target.value)} placeholder="e.g. 50"
              className="w-32 px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
          </div>
          <button onClick={applyConfig} disabled={savePrefsMutation.isPending}
            className="px-4 py-[7px] text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0d2780] disabled:opacity-50">Apply &amp; Save</button>
          <button onClick={resetConfig} className="px-3 py-[7px] text-sm font-medium text-gray-600 hover:text-[#0055a9]">Reset to default</button>
          <span className="text-[11px] text-[rgba(14,19,27,0.45)]">e.g. “D / C, B / A” — D alone, C &amp; B together, A alone. Saved to your account.</span>
        </div>
      )}

      {isLoading ? (
        <div className="p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0055a9] border-t-transparent mx-auto" /></div>
      ) : isError ? (
        <div className="bg-white rounded-xl border border-[#E8EBF1] p-10 text-center text-sm text-[#B91C1C]">Failed to load dashboard. <button onClick={() => refetch()} className="underline">Retry</button></div>
      ) : (
        <>
          {/* Summary */}
          {(() => {
            const totalBins = gridSections.length * config.binsPerSection;
            const runningLow = (data!.bins || []).filter((b: Bin) => b.totalBoxes > 0 && b.totalBoxes <= LOW_BOX).length;
            const free = Math.max(0, totalBins - data!.summary.binsUsed);
            return (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard label="Total Boxes" value={fmt(data!.summary.totalBoxes)} accent="text-[#0055a9]" note="on hand" />
                <StatCard label="Total Net Wt" value={`${fmt(data!.summary.netWeight)} kg`} accent="text-emerald-700" note="net weight" />
                <StatCard label="Bins in use" value={`${data!.summary.binsUsed}/${totalBins}`} accent="text-[#0E131B]" note={`${free} bins free`} />
                <StatCard label="Running low" value={fmt(runningLow)} accent={runningLow > 0 ? 'text-red-600' : 'text-emerald-700'} note={`${LOW_BOX} boxes or fewer`} />
              </div>
            );
          })()}

          {data!.summary.totalBoxes === 0 && (
            <div className="text-xs text-[rgba(14,19,27,0.5)] bg-[#F8F9FC] border border-[#E8EBF1] rounded-lg px-3 py-2">
              No cartons have a location yet — set a <b>Location</b> (e.g. <b>A-1</b>) on rows in Yarn Master and the slots below fill in.
            </div>
          )}

          {/* Mobile: section tabs (A B C D…) then the active section's bins as row cards.
              Every bin slot in the section renders a row — including unused ones (shown as
              0/empty) — so the mobile list matches the desktop grid, which always draws every
              column and only shades empty cells rather than omitting them. */}
          {(() => {
            const activeSec = mobileSection && gridSections.includes(mobileSection) ? mobileSection : gridSections[0];
            const slots = colNums.map((n) => {
              const location = `${activeSec}-${n}`;
              return { location, bin: binLookup[location] as Bin | undefined };
            });
            const t = sectionTotals(activeSec);
            return (
              <div className="md:hidden space-y-3">
                <div className="flex gap-1.5 overflow-x-auto scrollbar-hide -mx-2 px-2 pb-0.5">
                  {gridSections.map((sec) => {
                    const on = activeSec === sec;
                    const st = sectionTotals(sec);
                    return (
                      <button key={sec} onClick={() => setMobileSection(sec)}
                        className={`shrink-0 min-w-[48px] px-3 py-1.5 rounded-xl text-center transition-colors ${on ? 'bg-[#0055a9] text-white' : 'bg-white border border-[#E1E6EF] text-[#0E131B]'}`}>
                        <div className="text-base font-bold leading-none">{sec}</div>
                        <div className={`text-[9px] font-semibold mt-0.5 tabular-nums ${on ? 'text-white/70' : 'text-[rgba(14,19,27,0.4)]'}`}>{st.boxes} box</div>
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center gap-3 bg-white rounded-xl border border-[#E8EBF1] px-3.5 py-3">
                  <div className="w-10 h-10 rounded-lg bg-[#14181d] text-white flex items-center justify-center text-lg font-bold shrink-0">{activeSec}</div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-[#1B202A]">Section {activeSec}</p>
                    <p className="text-[11px] font-mono text-[rgba(14,19,27,0.5)]">{t.boxes} BOX · {fmt(t.net)} KG</p>
                  </div>
                </div>

                <div className="space-y-2">
                  {slots.length === 0 ? (
                    <div className="bg-white rounded-xl border border-[#E8EBF1] p-6 text-center text-sm text-[rgba(14,19,27,0.5)]">No stock in section {activeSec}.</div>
                  ) : slots.map(({ location, bin }) => {
                    const clickable = !!bin;
                    const netLow = !!bin && bin.totalBoxes > 0 && config.lowStockThreshold > 0 && bin.netWeight < config.lowStockThreshold;
                    const sh = binShade(bin ? bin.totalBoxes : 0, netLow);
                    const top = bin?.yarns[0];
                    const topLabel = top ? ([top.color, top.colorCode].filter(Boolean).join(' · ') || top.yarnName) : 'Empty';
                    const meta = !bin ? '' : sh.low ? 'LOW' : (bin.yarns.length > 1 ? `${bin.yarns.length} LOTS` : '1 LOT');
                    return (
                      <button key={location} disabled={!clickable} onClick={() => clickable && setHistoryLoc(location)}
                        className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left ${sh.border} ${sh.bg} ${clickable ? '' : 'cursor-default'}`}>
                        <div className="w-14 shrink-0">
                          <div className={`text-sm font-bold font-mono ${sh.code}`}>{location}</div>
                          {meta && <div className={`text-[9px] font-bold uppercase tracking-wider ${sh.low ? 'text-red-500' : 'text-[rgba(14,19,27,0.4)]'}`}>{meta}</div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-semibold truncate ${bin ? 'text-[#0E131B]' : 'text-[rgba(14,19,27,0.35)]'}`} title={top?.yarnName}>{topLabel}</div>
                          {bin && <div className="text-[11px] font-mono text-[rgba(14,19,27,0.5)]">{fmt(bin.netWeight)} KG</div>}
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`text-xl font-bold tabular-nums leading-none ${sh.wt}`}>{bin ? bin.totalBoxes : 0}</div>
                          <div className="text-[9px] font-mono text-[rgba(14,19,27,0.4)] mt-0.5">BOX</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Horizontal warehouse grid (desktop): sections are ROWS, bins run left→right.
              Each group is a bordered block (gap between groups); merged section-rows sit inside one block. */}
          <div className="hidden md:block space-y-4">
            {config.groups.map((group, gi) => (
              <div key={gi} className="bg-white rounded-xl border border-[#E8EBF1] overflow-hidden">
                <div className="overflow-x-auto">
                  <div className="min-w-max">
                    {/* Column-number header */}
                    <div className="flex border-b border-[#E8EBF1] bg-[#F8F9FC]">
                      <div className="sticky left-0 z-20 bg-[#F8F9FC] w-24 shrink-0 border-r border-[#E8EBF1] px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[rgba(14,19,27,0.45)]">Section</div>
                      {colNums.map(n => (
                        <div key={n} className="w-32 shrink-0 border-r border-[#EEF1F6] px-1.5 py-1.5 text-center text-[10px] font-bold text-[rgba(14,19,27,0.4)] tabular-nums">{n}</div>
                      ))}
                    </div>
                    {group.map((sec, si) => <SectionRow key={sec} sec={sec} last={si === group.length - 1} />)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Locations outside the configured grid */}
          {otherBins.length > 0 && (
            <div className="bg-white rounded-xl border border-[#E8EBF1] p-3">
              <p className="text-xs font-semibold text-[rgba(14,19,27,0.5)] uppercase tracking-wider mb-2">Other locations</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
                {otherBins.map((bin: Bin) => <BinCell key={bin.location} id={bin.location} bin={bin} onClick={setHistoryLoc} threshold={config.lowStockThreshold} />)}
              </div>
            </div>
          )}
        </>
      )}

      {historyLoc && <BoxHistoryModal location={historyLoc} onClose={() => setHistoryLoc(null)} />}
    </motion.div>
  );
};

export default YarnDashboard;
